import React, { useEffect, useState } from "react";
import { Box, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import chatBgPattern from "../../assets/chat-bg-pattern.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ChatiaDialog from "../ChatiaDialog";
import MarkdownWrapper from "../MarkdownWrapper";

const useStyles = makeStyles((theme) => ({
  editorShell: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  messagesList: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    flexGrow: 1,
    padding: 20,
    overflowY: "auto",
    minHeight: 178,
    minWidth: "min(500px, calc(100vw - 64px))",
    borderRadius: 12,
    border: theme.mode === "light" ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.08)",
    backgroundColor: theme.mode === "light" ? "#cfd2d6" : "#0e1418",
    ...theme.scrollbarStyles,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      backgroundImage: `url(${chatBgPattern})`,
      backgroundRepeat: "repeat",
      opacity: theme.mode === "light" ? 1 : 0.35,
      pointerEvents: "none",
    },
    [theme.breakpoints.down("xs")]: {
      minWidth: "auto",
      padding: 14,
    },
  },
  messagePreviewRow: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    width: "100%",
    justifyContent: "flex-end",
  },
  messageRight: {
    fontSize: 13,
    minWidth: 100,
    maxWidth: 510,
    height: "auto",
    display: "inline-block",
    position: "relative",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    backgroundColor: theme.mode === "light" ? "#e9edf2" : "#192535",
    color: theme.mode === "light" ? "#1a1a1a" : "#e9edf2",
    boxShadow: theme.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
  },
  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },
  inputPaper: {
    display: "flex",
    alignItems: "center",
    borderRadius: 12,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  inputmsg: {
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    display: "flex",
    width: "100%",
    borderRadius: 12,
    border: theme.mode === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.10)",
  },
  inputBase: {
    width: "100%",
    minHeight: 104,
    maxHeight: 180,
    padding: "14px 16px",
    flex: 1,
    border: 0,
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: 14,
    lineHeight: 1.45,
    background: "transparent",
    color: theme.mode === "light" ? "#111b21" : "#e9edef",
    "&::placeholder": {
      color: theme.mode === "light" ? "#667781" : "#8696a0",
    },
  },
}));

const EditMessageModal = ({ open, onClose, onSave, onMessageUpdated, message }) => {
  const classes = useStyles();
  const [editedMessage, setEditedMessage] = useState("");

  useEffect(() => {
    if (open) {
      setEditedMessage(message?.body || "");
    }
  }, [open, message]);

  const handleSave = async () => {
    const body = editedMessage.trim();
    if (!body || !message?.id) return;

    const payload = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body,
      quotedMsg: null,
    };

    const { data } = await api.post(`/messages/edit/${message.id}`, payload);
    const fallbackMessage = {
      ...message,
      body,
      isEdited: true,
      updatedAt: new Date().toISOString(),
    };
    const updatedMessage = data?.message?.id ? data.message : data?.id ? data : fallbackMessage;
    if (onMessageUpdated) onMessageUpdated(updatedMessage);
    if (onSave) onSave();
    onClose(false);
  };

  const previewTime = (() => {
    const value = message?.createdAt || message?.updatedAt;
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  })();

  return (
    <ChatiaDialog
      open={open}
      onClose={() => onClose(false)}
      title={i18n.t("messageOptionsMenu.edit") || "Editar"}
      maxWidth="sm"
      contentDividers
      onSave={handleSave}
      saveText="Salvar"
      saveDisabled={!editedMessage.trim()}
    >
      <Box className={classes.editorShell}>
        <Box className={classes.messagesList}>
          <Box className={classes.messagePreviewRow}>
            <Box component="div" className={classes.messageRight}>
              <Box className={classes.textContentItem}>
                <MarkdownWrapper>{editedMessage || ""}</MarkdownWrapper>
                <span className={classes.timestamp}>
                  {previewTime ? `${i18n.t("messagesResponsive.status.edited")} ${previewTime}` : i18n.t("messagesResponsive.status.edited")}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper
          component="form"
          className={classes.inputPaper}
          onSubmit={(event) => {
            event.preventDefault();
            handleSave();
          }}
        >
          <Box className={classes.inputmsg}>
            <textarea
              className={classes.inputBase}
              autoFocus
              placeholder="Digite a nova mensagem"
              value={editedMessage}
              onChange={(event) => setEditedMessage(event.target.value)}
              aria-label={i18n.t("messageOptionsMenu.edit") || "Editar"}
            />
          </Box>
        </Paper>
      </Box>
    </ChatiaDialog>
  );
};

export default EditMessageModal;
